/* eslint-disable complexity */
import ClearButton from './ClearButton'
import Divider from '../Divider/Divider'
import Downshift from 'downshift'
import EmptyState from './EmptyState/EmptyState'
import SearchInput from '../SearchInput/SearchInput'
import classes from './Combobox.module.scss'
import natsort from 'natsort'
import { HiCheck } from 'react-icons/hi'
import { Tooltip } from '../..'
import {
  maximumGroupListDescription,
  maximumListDescription
} from './MaximumListDescription'
import {
  useEffect,
  useRef,
  useState
} from 'react'
import type {
  ButtonShape,
  DropdownPlacement,
  GroupedOptions,
  OptionType,
  Options,
  Placement
} from '@eggplant/types'
import type {
  CSSProperties,
  ReactNode,
  UIEventHandler
} from 'react'
import type {
  ControllerStateAndHelpers,
  DownshiftState,
  GetItemPropsOptions,
  StateChangeOptions
} from 'downshift'

export interface Props {
  customValidity?: string
  groupedOptions?: GroupedOptions
  initialGroupedOptions?: GroupedOptions
  options?: Options
  initialOptions?: Options
  isRequired?: boolean
  hasSearch?: boolean
  hasClearButton?: boolean
  onChange: (value: string) => void
  renderButton: (props: ButtonShape) => void
  value?: string
  isDisabled?: boolean
  noOptionsPlaceholder?: ReactNode
  isFilter?: boolean
  isFilterFixedMinMaxWidth?: boolean
  externalQuery?: ReactNode
  onClear?: () => void
  isCustomBoxShadow?: boolean
  optionsLimit?: number
  placement?: Placement
  dropdownPlacement?: DropdownPlacement
}

const MAXIMUM_OPTIONS_COUNT = 500

const itemToString = (option?: OptionType | null) => option
  ? option.value
  : ''

const limitAndGroupOptions = (groupedOptions: GroupedOptions, limit: number): GroupedOptions => groupedOptions.reduce<[GroupedOptions, number]>(
  ([acc, remainingLimit], group) => {
    if (remainingLimit <= 0) return [acc, remainingLimit]

    const optionsToTake = group.options.slice(0, remainingLimit)
    const updatedLimit = remainingLimit - optionsToTake.length

    return [
      [
        ...acc,
        ...optionsToTake.length > 0
          ? [{ ...group, options: optionsToTake }]
          : []
      ],
      updatedLimit
    ]
  },
  [[], limit]
)[0]

const Combobox = ({
  customValidity = 'Please select an item in the list.',
  groupedOptions = [],
  options = [],
  onChange,
  renderButton,
  initialOptions = [],
  initialGroupedOptions = [],
  isRequired = false,
  value = '',
  isDisabled = false,
  noOptionsPlaceholder,
  hasSearch = true,
  hasClearButton = true,
  isFilter = false,
  isFilterFixedMinMaxWidth = true,
  externalQuery,
  onClear,
  placement,
  dropdownPlacement = 'bottom',
  optionsLimit = 0
}: Props) => {
  // This help controls the InputText's input
  const [input, setInput] = useState('')
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const inputRef = useRef<HTMLInputElement>(null)
  const comboBoxRef = useRef<HTMLDivElement>(null)
  const [defaultOptionCount, setDefaultOptionCount] = useState(optionsLimit)

  const sorter = natsort({ insensitive: true })

  // eslint-disable-next-line fp/no-mutating-methods
  const sortedOptions = options.sort((current, next) =>
    sorter(current.label, next.label)
  )

  // eslint-disable-next-line fp/no-mutating-methods
  const sortedGroupOptions = groupedOptions.sort((current, next) =>
    sorter(current.label, next.label)
  )

  const searchedOptions = input
    ? sortedOptions.filter(option =>
      option.label.toLowerCase().includes(input.toLowerCase())
    )
    : sortedOptions

  const searchedGroupedOptions = input
    ? sortedGroupOptions.map(group => (
      {
        ...group,
        options: group.options.filter(option =>
          option.label.toLowerCase().includes(input.toLowerCase()))
      }
    ))
    : sortedGroupOptions

  const filteredOptions = defaultOptionCount
    ? searchedOptions.slice(0, defaultOptionCount)
    : searchedOptions

  const filteredGroupedOptions = defaultOptionCount
    ? limitAndGroupOptions(searchedGroupedOptions, defaultOptionCount)
    : searchedGroupedOptions

  const hasInitialOptions = !!filteredOptions.length || !!filteredGroupedOptions.map(group => group.options).flat().length

  const handleResize = () => {
    setWindowWidth(window.innerWidth)
  }

  window.addEventListener('resize', handleResize)

  const onInputValueChange = (inputValue: string | null, stateAndHelpers: ControllerStateAndHelpers<OptionType>) => {
    const optionsNumber = (stateAndHelpers.selectedItem?.value === inputValue) || inputValue === ''
      ? defaultOptionCount || optionsLimit
      : optionsLimit

    optionsLimit && setDefaultOptionCount(optionsNumber)
    setInput(inputValue || '')
  }

  const onOuterClick = () => {
    optionsLimit && setDefaultOptionCount(optionsLimit)
    setInput('')
  }

  const handleSelection = (option: OptionType | null) => {
    if (!option || option.value === value) return

    onChange(option.value)

    setInput('')
  }

  // AUB-2625
  const stateReducer = (state: DownshiftState<OptionType>, changes: StateChangeOptions<OptionType>) => {
    if (changes.type === Downshift.stateChangeTypes.keyDownEscape) {
      return {
        ...changes,
        selectedItem: state.selectedItem,
        highlightedIndex: null,
        isOpen: false,
        inputValue: ''
      }
    }

    // AUB-17991
    return {
      ...changes,
      inputValue: ''
    }
  }

  const renderOptionLabel = (item: OptionType) =>
    <div className={classes.checkboxStyle}>
      <div className={classes.textStyle}>
        {item.label}
      </div>
      {item.value === value &&
        <HiCheck
          aria-hidden
          className={classes.CheckIcon}
          size={16}
        />}
    </div>

  const renderInitialOptions = (getItemProps: (initialOptions: GetItemPropsOptions<OptionType>) => OptionType) => initialOptions.length
    ? (
      <>
        {
          initialOptions.map(item =>
            <li
              key={item.value}
              {...getItemProps({ item })}
            >
              <Tooltip label={item.label}>
                {renderOptionLabel(item)}
              </Tooltip>
            </li>
          )
        }
        <Divider />
      </>
    )
    : null

  const renderOptions = (getItemProps: (filteredOptions: GetItemPropsOptions<OptionType>) => OptionType) => {
    const renderOptionsList = filteredOptions.length
      ? filteredOptions.map(item =>
        <li
          key={item.value}
          {...getItemProps({ item })}
        >
          <Tooltip label={item.label}>
            {renderOptionLabel(item)}
          </Tooltip>
        </li>
      )
      : null

    return filteredOptions.length
      ? renderOptionsList
      : <EmptyState />
  }

  const renderInitialGroupedOptions = (getItemProps: ({ item }: any) => OptionType) => initialGroupedOptions.length
    ? initialGroupedOptions.map(group =>
      <>
        <strong className={classes.GroupStyle}>
          {group.label}
        </strong>
        {
          group.options.map(item =>
            <li
              key={item.value}
              {...getItemProps({ item })}
              className={classes.GroupStyle}
            >
              <Tooltip label={item.label}>
                {renderOptionLabel(item)}
              </Tooltip>
            </li>
          )
        }
      </>
    )
    : null

  const renderGroupedOptions = (getItemProps: ({ item }: any) => OptionType) => {
    const availableOptions = filteredGroupedOptions.map(group => group.options).flat()

    return availableOptions.length
      ? filteredGroupedOptions.map(group =>
        <>
          <strong className={classes.GroupStyle}>
            {group.label}
          </strong>
          {
            group.options.map(item =>
              <li
                key={item.value}
                {...getItemProps({ item })}
                className={classes.GroupStyle}
              >
                <Tooltip label={item.label}>
                  {renderOptionLabel(item)}
                </Tooltip>
              </li>
            )
          }
        </>
      )
      : <EmptyState />
  }

  const validate = () => {
    const ref = inputRef.current

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!ref) return

    // reset CustomValidity when value changed
    ref.setCustomValidity('')
  }

  const handleClear = () => {
    onChange('')

    onClear && onClear()
  }

  const handleScroll: UIEventHandler<HTMLDivElement> | undefined = event => {
    if (!optionsLimit) return

    const {
      offsetHeight,
      scrollHeight,
      scrollTop
    } = event.target as HTMLDivElement

    // TODO: Allow a tolerance before hitting the bottom
    const isAtBottom = scrollTop + offsetHeight >= scrollHeight

    if (isAtBottom && defaultOptionCount) {
      if (defaultOptionCount < MAXIMUM_OPTIONS_COUNT) setDefaultOptionCount(defaultOptionCount + optionsLimit)
    }
  }

  const clearSectionClassName = externalQuery
    ? classes.clearSection + ' ' + classes.justifySpaceBetween
    : classes.clearSection

  const renderClearSection =
    <div className={clearSectionClassName}>
      {
        externalQuery && externalQuery
      }
      {
        value && <ClearButton onClear={handleClear} />
      }
    </div>

  useEffect(validate, [value])

  const getComboBoxClasses = () => {
    const enabledClass = !isDisabled
      ? classes.Enabled
      : ''

    const hasControlledWidthClass = isFilter && isFilterFixedMinMaxWidth
      ? classes.FilterComboboxMinMaxWidth
      : ''

    const isBondedLeftClass = placement === 'left'
      ? classes.isLeftBondedFilters
      : ''

    const isBondedRightClass = placement === 'right'
      ? classes.isRightBondedFilters
      : ''

    const comboBoxClasses = [
      classes.Combobox,
      enabledClass,
      hasControlledWidthClass,
      isBondedLeftClass,
      isBondedRightClass
    ].filter(className => className)

    return comboBoxClasses.join(' ')
  }

  const getComboBoxLeftPositionStyle = (comboBoxRef: React.RefObject<HTMLDivElement>): CSSProperties => {
    if (!comboBoxRef.current) {
      return { left: '0px' }
    }

    const comboxBoxPos = comboBoxRef.current.getBoundingClientRect().left + 410

    if (comboxBoxPos > windowWidth) {
      return { left: `${windowWidth - comboxBoxPos}px` }
    }

    return { left: '0px' }
  }

  const getComboBoxRightPositionStyle = (comboBoxRef: React.RefObject<HTMLDivElement>): CSSProperties => {
    if (!comboBoxRef.current) {
      return { top: '-2px', left: '115px' }
    }

    const comboxBoxPos = comboBoxRef.current.offsetWidth

    return { top: '-2px', left: `${comboxBoxPos + 5}px` }
  }

  const handleInputClear = () => { setInput('') }

  return (
    <div ref={comboBoxRef}>
      <Downshift
        inputValue={input}
        itemToString={itemToString}
        key={value} // resolve AUB-19498 where parent call to change value downshift state is not updated
        onChange={handleSelection}
        onInputValueChange={onInputValueChange}
        onOuterClick={onOuterClick}
        stateReducer={stateReducer}
      >
        {
        // eslint-disable-next-line complexity
          ({
            getInputProps,
            getItemProps,
            getMenuProps,
            getRootProps,
            getToggleButtonProps,
            isOpen
          }) =>
            <div
              className={getComboBoxClasses()}
              {...getRootProps(undefined, { suppressRefError: true })}
              aria-disabled={isDisabled}
              role={undefined}
            >
              <input
                checked={!!value}
                onInvalid={(event: React.ChangeEvent<HTMLInputElement>) => event.target.setCustomValidity(customValidity)}
                readOnly
                ref={inputRef}
                required={isRequired}
                type='checkbox'
              />
              {renderButton(getToggleButtonProps())}
              {
                isOpen && !isDisabled &&
                  <div
                    className={classes.dropdownWrapper}
                    style={
                      dropdownPlacement === 'bottom'
                        ? getComboBoxLeftPositionStyle(comboBoxRef)
                        : getComboBoxRightPositionStyle(comboBoxRef)
                    }
                  >
                    {
                      options.length || groupedOptions.length
                        ? (
                          <>
                            {
                              hasSearch &&
                                <div className={classes.searchWrapper}>
                                  <SearchInput
                                    {
                                      ...getInputProps()
                                    }
                                    isAutoFocus
                                    onClear={handleInputClear}
                                  />
                                </div>
                            }
                            <ul
                              {...getMenuProps()}
                              onScroll={handleScroll}
                            >
                              {
                                hasInitialOptions && renderInitialOptions(getItemProps)
                              }
                              {
                                hasInitialOptions && renderInitialGroupedOptions(getItemProps)
                              }
                              {
                                !!options.length && renderOptions(getItemProps)
                              }
                              {
                                !!groupedOptions.length && renderGroupedOptions(getItemProps)
                              }
                              {
                                defaultOptionCount === MAXIMUM_OPTIONS_COUNT && options.length > MAXIMUM_OPTIONS_COUNT && maximumListDescription(options.length)
                              }
                              {
                                defaultOptionCount === MAXIMUM_OPTIONS_COUNT && groupedOptions.length > MAXIMUM_OPTIONS_COUNT && maximumGroupListDescription(groupedOptions.length)
                              }
                            </ul>
                            {
                              hasClearButton && (!!externalQuery || value) && renderClearSection
                            }
                          </>
                        )
                        : noOptionsPlaceholder
                    }
                  </div>
              }
            </div>
        }
      </Downshift>
    </div>
  )
}

export default Combobox
